import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  CalendarToday,
  PictureAsPdf,
  Download,
  Close,
  FilterList,
  DateRange,
} from '@mui/icons-material';

function CommuniquesPresse() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Données des communiqués de presse
  const [communiques, setCommuniques] = useState([
    /*
    {
      id: 1,
      titre: "Publication des résultats de l'enquête sur les indicateurs de pauvreté 2023",
      date: "15/03/2024",
      categorie: "Économie",
      fichier: "/pdf/communique-pauvrete-2023.pdf",
      resume: "L'INSBU publie les résultats de l'enquête nationale sur les indicateurs de pauvreté pour l'année 2023. Les données montrent une amélioration significative des conditions de vie dans plusieurs régions du pays.",
      image: "/images/communique1.jpg"
    },
    {
      id: 2,
      titre: "Indice des prix à la consommation - Décembre 2023",
      date: "15/01/2024",
      categorie: "Inflation",
      fichier: "/pdf/communique-ipc-decembre-2023.pdf",
      resume: "L'INSBU annonce le taux d'inflation pour le mois de décembre 2023. L'indice des prix à la consommation enregistre une stabilisation par rapport au mois précédent.",
      image: "/images/communique2.jpg"
    },
    {
      id: 3,
      titre: "Résultats du recensement agricole 2023-2024",
      date: "28/02/2024",
      categorie: "Agriculture",
      fichier: "/pdf/recensement-agricole-2024.pdf",
      resume: "Publication des résultats préliminaires du recensement agricole national 2023-2024. Les données révèlent une croissance de la production dans plusieurs filières agricoles.",
      image: "/images/communique3.jpg"
    },
    {
      id: 4,
      titre: "Lancement de la collecte des données pour l'enquête démographique et de santé",
      date: "10/01/2024",
      categorie: "Démographie",
      fichier: "/pdf/lancement-eds-2024.pdf",
      resume: "L'INSBU annonce le lancement de la collecte des données pour l'Enquête Démographique et de Santé 2024. Cette enquête fournira des indicateurs clés sur la santé et la population.",
      image: "/images/communique4.jpg"
    },
    {
      id: 5,
      titre: "Indicateurs du commerce extérieur - Troisième trimestre 2023",
      date: "15/11/2023",
      categorie: "Commerce",
      fichier: "/pdf/communique-commerce-t3-2023.pdf",
      resume: "Publication des statistiques du commerce extérieur pour le troisième trimestre 2023. Les données montrent une augmentation des exportations et une réduction du déficit commercial.",
      image: "/images/communique5.jpg"
    },
    {
      id: 6,
      titre: "Conférence de presse sur les comptes nationaux 2022",
      date: "05/12/2023",
      categorie: "Comptes nationaux",
      fichier: "/pdf/comptes-nationaux-2022.pdf",
      resume: "L'INSBU présente les résultats des comptes nationaux pour l'année 2022. L'économie nationale affiche une croissance positive malgré le contexte international difficile.",
      image: "/images/communique6.jpg"
    },
    
    {
      id: 7,
      titre: "Publication de l'annuaire statistique 2023",
      date: "20/02/2024",
      categorie: "Publication",
      fichier: "/pdf/annuaire-statistique-2023.pdf",
      resume: "L'INSBU publie l'édition 2023 de l'annuaire statistique national. Cet ouvrage de référence contient les principales statistiques socio-économiques du pays.",
      image: "/images/communique7.jpg"
    },
    {
      id: 8,
      titre: "Atelier de validation des indicateurs de développement durable",
      date: "15/02/2024",
      categorie: "Développement durable",
      fichier: "/pdf/atelier-odd-2024.pdf",
      resume: "L'INSBU organise un atelier de validation des indicateurs de développement durable en collaboration avec les partenaires techniques et financiers.",
      image: "/images/communique8.jpg"
    },*/
  ]);

  // États pour les filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [selectedYear, setSelectedYear] = useState('Toutes');
  const [selectedCommunique, setSelectedCommunique] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;

  // Catégories disponibles
  const categories = ['Tous', 'Économie', 'Inflation', 'Agriculture', 'Démographie', 'Commerce', 'Comptes nationaux', 'Publication', 'Développement durable'];
  
  // Années disponibles
  const years = ['Toutes','2026', '2025',  '2024', '2023', '2022', '2021'];

  // Filtrage des communiqués
  const filteredCommuniques = communiques.filter(communique => {
    const matchesSearch = communique.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         communique.resume.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Tous' || communique.categorie === selectedCategory;
    const matchesYear = selectedYear === 'Toutes' || communique.date.includes(selectedYear);
    
    return matchesSearch && matchesCategory && matchesYear;
  });

  // Pagination
  const pageCount = Math.ceil(filteredCommuniques.length / itemsPerPage);
  const paginatedCommuniques = filteredCommuniques.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Gestion du clic sur un communiqué
  const handleCommuniqueClick = (communique) => {
    setSelectedCommunique(communique);
    setOpenDialog(true);
  };

  // Téléchargement du fichier
  const handleDownload = (fichier) => {
    // Simuler le téléchargement
    const link = document.createElement('a');
    link.href = fichier;
    link.download = fichier.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('Tous');
    setSelectedYear('Toutes');
    setPage(1);
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <NavigationBar />
      
      <Container maxWidth="lg" style={{ paddingTop: '60px', paddingBottom: '40px' }}>
        {/* En-tête de la page */}
        <Box mb={4}>
          <Typography 
            variant="h5" 
            gutterBottom 
            style={{ 
              color: '#388E3C',
              fontWeight: 'bold',
              marginBottom: '10px'
            }}
          >
            Communiqués de presse
          </Typography>
          
          <Typography 
            variant="body1" 
            paragraph 
            style={{ 
              fontSize: '1.1rem', 
              color: '#666',
              maxWidth: '1500px'
            }}
          >
            Retrouvez ici tous les communiqués de presse publiés par l'Institut National de la Statistique du Burundi (INSBU). 
            Ces documents officiels présentent les principales statistiques, les résultats d'enquêtes et les informations importantes concernant l'activité statistique nationale.
          </Typography>
        </Box>

        {/* Barre de recherche et filtres */}
        <Box mb={4} p={3} sx={{ backgroundColor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Barre de recherche */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher un communiqué..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Filtres */}
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={2}>
                <Chip
                  icon={<FilterList />}
                  label={`Catégorie: ${selectedCategory}`}
                  variant={selectedCategory !== 'Tous' ? "filled" : "outlined"}
                  color="#388E3C"
                  onClick={() => {
                    const index = categories.indexOf(selectedCategory);
                    const nextIndex = (index + 1) % categories.length;
                    setSelectedCategory(categories[nextIndex]);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                
                <Chip
                  icon={<DateRange />}
                  label={`Année: ${selectedYear}`}
                  variant={selectedYear !== 'Toutes' ? "filled" : "outlined"}
                  color="#388E3C"
                  onClick={() => {
                    const index = years.indexOf(selectedYear);
                    const nextIndex = (index + 1) % years.length;
                    setSelectedYear(years[nextIndex]);
                  }}
                  style={{ cursor: 'pointer' }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  color='#388E3C'
                  onClick={resetFilters}
                  style={{ marginLeft: isMobile ? 0 : 'auto' }}
                >
                  Réinitialiser
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Statistiques */}
        <Box mb={3}>
          <Typography variant="body2" color="textSecondary">
            {filteredCommuniques.length} communiqué{filteredCommuniques.length !== 1 ? 's' : ''} trouvé{filteredCommuniques.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Liste des communiqués */}
        {filteredCommuniques.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {paginatedCommuniques.map((communique) => (
                <Grid item xs={12} sm={6} md={4} key={communique.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => handleCommuniqueClick(communique)}
                  >
                    {/* Image ou placeholder */}
                    <Box
                      sx={{
                        height: 140,
                        backgroundColor: '#e8f5e9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}
                    >
                      <PictureAsPdf sx={{ fontSize: 60, color: '#388E3C' }} />
                      <Chip
                        label={communique.categorie}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          backgroundColor: '#388E3C',
                          color: 'white'
                        }}
                      />
                    </Box>

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <CalendarToday sx={{ fontSize: 16, color: '#666', mr: 1 }} />
                        <Typography variant="caption" color="textSecondary">
                          {communique.date}
                        </Typography>
                      </Box>
                      
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          color: '#333',
                          lineHeight: 1.3,
                          mb: 2
                        }}
                      >
                        {communique.titre}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {communique.resume}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }}>
                      <Button 
                        size="small" 
                        color="#388E3C"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(communique.fichier);
                        }}
                      >
                        Télécharger
                      </Button>
                      <Button size="small" color="#E30613">
                        En savoir plus
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {pageCount > 1 && (
              <Box mt={4} display="flex" justifyContent="center">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="#388E3C"
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            )}
          </>
        ) : (
          // Aucun résultat
          <Box 
            textAlign="center" 
            py={8} 
            sx={{ 
              backgroundColor: 'white', 
              borderRadius: 2,
              boxShadow: 1
            }}
          >
            <Search sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Aucun communiqué trouvé
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Aucun communiqué ne correspond à votre recherche. Essayez avec d'autres mots-clés.
            </Typography>
            <Button 
              variant="outlined" 
              color='#388E3C'
              sx={{ mt: 2 }}
              onClick={resetFilters}
            >
              Voir tous les communiqués
            </Button>
          </Box>
        )}

        {/* Section d'information */}
        <Box mt={6} p={3} sx={{ backgroundColor: '#e8f5e9', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom style={{ color: '#388E3C' }}>
            À propos des communiqués de presse
          </Typography>
          <Typography variant="body2" paragraph>
            Les communiqués de presse de l'INSBU sont des documents officiels qui présentent les principaux 
            résultats statistiques, les lancements d'enquêtes, les publications importantes et les événements 
            majeurs de l'institut.
          </Typography>
           {/*
          <Typography variant="body2" paragraph>
            Pour recevoir automatiquement nos communiqués de presse par email, vous pouvez vous inscrire 
            à notre liste de diffusion. Pour toute demande d'information complémentaire, contactez notre 
            service de communication.
          </Typography>
          <Button variant="contained" color="primary">
            S'inscrire à la newsletter
          </Button> */}
        </Box>
      </Container>

      {/* Dialog pour afficher les détails d'un communiqué */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedCommunique && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" style={{ color: '#388E3C' }}>
                  {selectedCommunique.titre}
                </Typography>
                <IconButton onClick={() => setOpenDialog(false)} size="small">
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent dividers>
              <Box mb={2} display="flex" alignItems="center">
                <CalendarToday sx={{ fontSize: 16, color: '#666', mr: 1 }} />
                <Typography variant="body2" color="textSecondary" mr={2}>
                  Publié le: {selectedCommunique.date}
                </Typography>
                <Chip 
                  label={selectedCommunique.categorie} 
                  size="small" 
                  sx={{ backgroundColor: '#388E3C', color: 'white' }}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" paragraph>
                {selectedCommunique.resume}
              </Typography>
              
              <Box mt={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Informations complémentaires
                </Typography>
                <Typography variant="body2">
                  Ce communiqué de presse est disponible en format PDF. Pour toute demande d'information 
                  complémentaire ou pour organiser une interview avec nos experts, veuillez contacter 
                  le service de communication de l'INSBU.
                </Typography>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button 
                onClick={() => setOpenDialog(false)}
                color="inherit"
              >
                Fermer
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<Download />}
                onClick={() => {
                  handleDownload(selectedCommunique.fichier);
                  setOpenDialog(false);
                }}
              >
                Télécharger le PDF
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Footer />
    </div>
  );
}

export default CommuniquesPresse;